import { useEffect, useRef } from "react";
import { Animated, Easing, StyleSheet, View } from "react-native";

const WAVEFORM_BAR_COUNT = 22;
const WAVEFORM_HEIGHT = 28;

function getBarColor(i: number, total: number): string {
    const t = total > 1 ? i / (total - 1) : 0;
    const r = Math.round(69 + (231 - 69) * t);
    const g = Math.round(210 + (72 - 210) * t);
    const b = Math.round(176 + (245 - 176) * t);
    return `rgb(${r},${g},${b})`;
}

interface Props {
    /** 0..1 audio meter from the recorder. */
    meter: number;
}

/**
 * 22 brand-gradient bars driven by an audio meter. Each bar tweens its
 * `height` (which can't use the native driver on any platform) on a
 * 90ms ease-out per meter tick. Center bars get more amplitude (envelope)
 * and a sin/random variation for a non-mechanical look.
 */
export function LiveWaveform({ meter }: Props) {
    const anims = useRef(
        Array.from({ length: WAVEFORM_BAR_COUNT }, () => new Animated.Value(4))
    ).current;

    useEffect(() => {
        const boosted = Math.pow(Math.max(0, meter), 0.6);
        anims.forEach((a, i) => {
            const centerDist = Math.abs(i - WAVEFORM_BAR_COUNT / 2) / (WAVEFORM_BAR_COUNT / 2);
            const centerFactor = 0.3 + 0.7 * (1 - centerDist);
            const variation = 0.55 + ((Math.sin(i * 1.7 + meter * 12) + 1) / 2) * 0.45;
            const target = 4 + boosted * (WAVEFORM_HEIGHT - 6) * centerFactor * variation;
            Animated.timing(a, {
                toValue: Math.min(Math.max(target, 4), WAVEFORM_HEIGHT - 2),
                duration: 90,
                easing: Easing.out(Easing.ease),
                // height cannot be native-driven on any platform.
                useNativeDriver: false
            }).start();
        });
    }, [meter, anims]);

    return (
        <View style={styles.row} accessibilityElementsHidden importantForAccessibility="no">
            {anims.map((anim, i) => (
                <Animated.View
                    key={i}
                    style={[
                        styles.bar,
                        { height: anim, backgroundColor: getBarColor(i, WAVEFORM_BAR_COUNT) }
                    ]}
                />
            ))}
        </View>
    );
}

const styles = StyleSheet.create({
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: 3,
        height: WAVEFORM_HEIGHT,
        width: "100%"
    },
    bar: {
        flex: 1,
        borderRadius: 2,
        minWidth: 3
    }
});
